import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  useGetAvailableContactsQuery,
  useGetEventByDateRangeQuery,
  useGetPendingEventsQuery,
} from 'services/event.service';
import { EventStatus } from 'utils/constants';
import { useAuth } from 'modules/auth/context/AuthContextV2';
import { IEvent, IContact } from 'types/event';

const LOCAL_STORAGE_KEY = 'dismissedEvents';

const PREDEFINED_UPDATES = {
  calendar: 'calendar',
  inviteFriends: 'inviteFriends',
  friends: 'friends',
  sendMessage: 'sendMessage',
} as const;

export type UpdateCard =
  | { type: 'event'; eventData: IEvent }
  | { type: 'calendar' }
  | { type: 'inviteFriends' }
  | { type: 'friends' }
  | { type: 'sendMessage' };

type DismissedEvent = {
  eventId: string;
  expiresAt: string;
};

const useUpdates = () => {
  const { user } = useAuth();

  const { data: laterEvents, refetch: refetchLaterEvents } =
    useGetEventByDateRangeQuery({
      startDate: dayjs()
        .startOf('week')
        .add(1, 'day')
        .add(0, 'week')
        .format('YYYY-MM-DD'),
      endDate: dayjs().endOf('week').add(1, 'year').format('YYYY-MM-DD'),
    });

  const { data: pendingEvents, refetch: refetchPendingEvents } =
    useGetPendingEventsQuery({
      page: 0,
      isPast: false,
    });

  const { data: availableContacts } = useGetAvailableContactsQuery();

  const filteredAvailableContacts = useMemo(() => {
    return availableContacts?.data?.filter((contact) => {
      return contact._doc?.userId !== user?.id;
    });
  }, [availableContacts, user]);

  const [updates, setUpdates] = useState<UpdateCard[]>([]);

  const getDismissedEvents = () => {
    let dismissedEvents: DismissedEvent[] = [];

    try {
      dismissedEvents = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEY) || '[]',
      ).filter((item: DismissedEvent) => {
        return dayjs(item.expiresAt).isAfter(dayjs());
      });
    } catch (error) {
      console.error(
        'Error getting dismissed events from local storage:',
        error,
      );
    }

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dismissedEvents));

    return dismissedEvents;
  };

  const [_dismissedEvents, setDismissedEvents] = useState<DismissedEvent[]>([]);

  const addEventToUpdates = (event: IEvent) => {
    setUpdates((prevUpdates) => [
      ...prevUpdates.filter(
        (update) =>
          update.type !== 'event' ||
          (update.type === 'event' && update.eventData._id !== event._id),
      ),
      { type: 'event', eventData: event },
    ]);
  };

  const pollEventsData = useMemo(() => {
    return pendingEvents?.filter((event: any) => {
      return (
        event.type === 'poll' &&
        event.event.contacts.some(
          (contact: any) => contact.userId === user?.id && contact.status === 0,
        )
      );
    });
  }, [pendingEvents, user]);

  const laterEventsData = useMemo(() => {
    return laterEvents?.filter((event: any) => {
      return event.event.contacts.some(
        (contact: any) => contact.userId === user?.id && contact.status === 0,
      );
    });
  }, [laterEvents, user]);

  useEffect(() => {
    const dismissedEvents = getDismissedEvents();

    const notAcceptedEvents = [
      ...(laterEventsData ?? []),
      ...(pollEventsData ?? []),
    ];

    const notDismissedEvents = notAcceptedEvents.filter(
      (event) =>
        !dismissedEvents.some((item) => item.eventId === event.event._id),
    );

    // Clear previous events and add new ones
    setUpdates((prevUpdates) =>
      prevUpdates.filter((update) => update.type !== 'event'),
    );

    for (const event of notDismissedEvents) {
      addEventToUpdates(event.event);
    }
  }, [pollEventsData, laterEventsData, _dismissedEvents]);

  useEffect(() => {
    const dismissedEvents = getDismissedEvents();

    //Calendar Update
    if (
      !dismissedEvents.some(
        (event) => event.eventId === PREDEFINED_UPDATES.calendar,
      )
    ) {
      setUpdates((prevUpdates) => [
        ...prevUpdates.filter((update) => update.type !== 'calendar'),
        { type: 'calendar' },
      ]);
    } else {
      setUpdates((prevUpdates) =>
        prevUpdates.filter((update) => update.type !== 'calendar'),
      );
    }

    //Invite Friends Update
    if (
      !dismissedEvents.some(
        (event) => event.eventId === PREDEFINED_UPDATES.inviteFriends,
      )
    ) {
      setUpdates((prevUpdates) => [
        ...prevUpdates.filter((update) => update.type !== 'inviteFriends'),
        { type: 'inviteFriends' },
      ]);
    } else {
      setUpdates((prevUpdates) =>
        prevUpdates.filter((update) => update.type !== 'inviteFriends'),
      );
    }

    //Friends Update
    if (
      !dismissedEvents.some(
        (event) => event.eventId === PREDEFINED_UPDATES.friends,
      ) &&
      filteredAvailableContacts &&
      filteredAvailableContacts?.length > 0
    ) {
      setUpdates((prevUpdates) => [
        ...prevUpdates.filter((update) => update.type !== 'friends'),
        { type: 'friends' },
      ]);
    } else {
      setUpdates((prevUpdates) =>
        prevUpdates.filter((update) => update.type !== 'friends'),
      );
    }
    //Send Message Update
    if (
      !dismissedEvents.some(
        (event) => event.eventId === PREDEFINED_UPDATES.sendMessage,
      )
    ) {
      setUpdates((prevUpdates) => [
        ...prevUpdates.filter((update) => update.type !== 'sendMessage'),
        { type: 'sendMessage' },
      ]);
    } else {
      setUpdates((prevUpdates) =>
        prevUpdates.filter((update) => update.type !== 'sendMessage'),
      );
    }
  }, [availableContacts]);

  const handleDismiss = (eventId: string, expiresAt?: Date) => {
    const dismissedEvents = [...getDismissedEvents()].filter(
      (event) => event.eventId !== eventId,
    );

    dismissedEvents.push({
      eventId,
      expiresAt: expiresAt
        ? dayjs(expiresAt).toISOString()
        : dayjs().add(10, 'year').toISOString(),
    });

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dismissedEvents));

    setDismissedEvents(dismissedEvents);

    //Clean up dismissed events
    setUpdates((prevUpdates) =>
      prevUpdates.filter(
        (update) =>
          !dismissedEvents.some((event) => event.eventId === update.type),
      ),
    );
  };

  useEffect(() => {
    refetchLaterEvents();
    refetchPendingEvents();
  }, [updates, laterEventsData, pollEventsData]);

  return { updates, handleDismiss };
};

export default useUpdates;
